import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../hooks/useLearner';
import useFocusOnMount from '../hooks/useFocusOnMount';
import Button from '../components/common/Button';
import CloudBand from '../components/common/CloudBand';
import s from './TopicPickerPage.module.css';

const TOPIC_CATEGORIES = [
  {
    key: 'ancient-civilizations',
    theme: 'Ancient Civilizations',
    titleKey: 'topics.ancientCivilizations',
    image: '/assets/theme-picker-ancient-civilizations.webp',
  },
  {
    key: 'magical-creatures',
    theme: 'Magical Creatures & Folklore',
    titleKey: 'topics.magicalCreatures',
    image: '/assets/theme-picker-magical-creatures.webp',
  },
  {
    key: 'animal-adventures',
    theme: 'Animal Adventures',
    titleKey: 'topics.animalAdventures',
    image: '/assets/theme-picker-animal-adventures.webp',
  },
  {
    key: 'epic-quests',
    theme: 'Epic Quests & Heroes',
    titleKey: 'topics.epicQuests',
    image: '/assets/theme-picker-epic-quests.webp',
  },
  {
    key: 'festivals',
    theme: 'Festivals & Celebrations',
    titleKey: 'topics.festivals',
    image: '/assets/theme-picker-festivals.webp',
  },
  {
    key: 'everyday-life',
    theme: 'Everyday Life & Friends',
    titleKey: 'topics.everydayLife',
    image: '/assets/theme-picker-everyday-life.webp',
  },
];

// ─── Theme prop config ─────────────────────────────────────────────────────
// desktop: positions from Figma 1440×1024 canvas (% of viewport)
// mobile:  positions from Figma 402×874 canvas (% of viewport)
// Negative values intentionally extend props off-screen — clipped by propsLayer.
const THEME_PROPS = {
  'ancient-civilizations': {
    desktop: [
      {
        // Stepped pyramid — lower left
        src: '/assets/theme-prop-07.webp',
        style: { left: '-32.847%', top: '56.543%', width: '69.861%', height: '55.859%' },
      },
      {
        // Stone guardian lion — lower right
        src: '/assets/theme-prop-08.webp',
        style: { left: '78.819%', top: '48.145%', width: '26.806%', height: '58.984%' },
      },
    ],
    mobile: [
      {
        // Pyramid — centered, bottom-anchored. height:auto lets natural
        // aspect ratio drive height so no object-fit gymnastics needed.
        src: '/assets/theme-prop-07.webp',
        style: { left: '0%', bottom: '0', width: '100%', height: 'auto', transform: 'translateX(-50%)' },
      },
    ],
  },
  'magical-creatures': {
    desktop: [
      {
        // Giant glowing mushroom — right edge
        src: '/assets/theme-prop-01.webp',
        style: { left: '74.306%', top: '-15.039%', width: '41.944%', height: '119.238%' },
      },
      {
        // Crystal cluster — lower left
        src: '/assets/theme-prop-02.webp',
        style: { left: '-1.736%', top: '68.359%', width: '21.875%', height: '35.84%' },
      },
    ],
    mobile: [
      {
        // Crystal only — no mushroom on mobile
        src: '/assets/theme-prop-02.webp',
        style: { left: '-11.940%', top: '66.590%', width: '78.358%', height: '41.991%' },
      },
    ],
  },
  'animal-adventures': {
    desktop: [
      {
        // Monkey on vines — top right, flush to viewport edge.
        // object-position pins the artwork to the top-right of its bounding box
        // so the monkey stays touching the right edge at all viewport aspect
        // ratios (otherwise object-fit: contain letterboxes it at non-1440:1024).
        src: '/assets/theme-prop-10.webp',
        style: {
          left: '71.597%',
          top: '0%',
          width: '28.403%',
          height: '75.879%',
          objectPosition: 'right top',
        },
      },
      {
        // Red panda + turtle — lower left, rotated
        src: '/assets/theme-prop-09.webp',
        style: {
          left: '-6.597%',
          top: '65.332%',
          width: '32.627%',
          height: '46.502%',
          transform: 'rotate(-8.18deg)',
          transformOrigin: 'center',
        },
      },
    ],
    mobile: [
      {
        // Monkey — upper right, cropped to show right portion
        src: '/assets/theme-prop-10.webp',
        style: {
          left: '33.831%',
          top: '0%',
          width: '67.910%',
          height: '59.267%',
          objectFit: 'cover',
          objectPosition: 'right center',
        },
      },
      {
        // Red panda + turtle — lower left, rotated
        src: '/assets/theme-prop-09.webp',
        style: {
          left: '-25.622%',
          top: '68.650%',
          width: '84.202%',
          height: '39.254%',
          transform: 'rotate(-8.18deg)',
          transformOrigin: 'center',
        },
      },
    ],
  },
  'epic-quests': {
    desktop: [
      {
        // Stone pillar + ivy — left edge, full height
        src: '/assets/theme-prop-12.webp',
        style: { left: '-15.069%', top: '0%', width: '36.875%', height: '106.836%' },
      },
      {
        // Vine swing + characters — upper right
        src: '/assets/theme-prop-11.webp',
        style: { left: '62.222%', top: '0%', width: '32.639%', height: '47.461%' },
      },
    ],
    mobile: [
      {
        // Pillar — left edge, different scale
        src: '/assets/theme-prop-12.webp',
        style: { left: '-57.960%', top: '0%', width: '115.920%', height: '109.954%' },
      },
      {
        // Monstera leaf — lower right, flipped + rotated
        src: '/assets/theme-prop-14.webp',
        style: {
          left: '34.328%',
          top: '56.522%',
          width: '106.762%',
          height: '53.422%',
          transform: 'scaleY(-1) rotate(-155.77deg)',
          transformOrigin: 'center',
        },
      },
    ],
  },
  'festivals': {
    desktop: [
      {
        // Hanging lanterns — full-width top banner
        src: '/assets/theme-prop-13.webp',
        style: { left: '2.5%', top: '-2.734%', width: '93.611%', height: '27.93%' },
      },
      {
        // Dragon + characters — lower right
        src: '/assets/theme-prop-06.webp',
        style: { left: '68.333%', top: '61.523%', width: '33.681%', height: '40.723%' },
      },
    ],
    mobile: [
      {
        // Lanterns — centered banner at top. height:auto lets natural
        // aspect ratio drive height so no object-fit gymnastics needed.
        src: '/assets/theme-prop-13.webp',
        style: { left: '50%', top: '0', width: '250%', height: 'auto', transform: 'translateX(-50%)' },
      },
      {
        // Dragon + characters — lower right
        src: '/assets/theme-prop-06.webp',
        style: { left: '40.796%', top: '68.878%', width: '85.323%', height: '33.752%' },
      },
    ],
  },
  'everyday-life': {
    desktop: [
      {
        // Cat pouring tea — lower left
        src: '/assets/theme-prop-05.webp',
        style: { left: '-2.083%', top: '69.141%', width: '25.833%', height: '34.668%' },
      },
      {
        // Red panda eating bun — lower right
        src: '/assets/theme-prop-04.webp',
        style: { left: '70.278%', top: '70.703%', width: '18.681%', height: '34.668%' },
      },
      {
        // Potted plant — lower right, rotated
        src: '/assets/theme-prop-03.webp',
        style: {
          left: '84.345%',
          top: '67.02%',
          width: '18.7%',
          height: '38.381%',
          transform: 'rotate(5.02deg)',
          transformOrigin: 'center',
        },
      },
    ],
    mobile: [
      {
        // Cat — lower left, off left edge
        src: '/assets/theme-prop-05.webp',
        style: { left: '-19.154%', top: '68.650%', width: '78.109%', height: '34.210%' },
      },
      {
        // Potted plant — lower right, slightly rotated
        src: '/assets/theme-prop-03.webp',
        style: {
          left: '62.502%',
          top: '67.334%',
          width: '57.585%',
          height: '39.293%',
          transform: 'rotate(3.85deg)',
          transformOrigin: 'center',
        },
      },
    ],
  },
};

export default function TopicPickerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeLearner } = useLearner();
  const [selectedTopic, setSelectedTopic] = useState(TOPIC_CATEGORIES[0]);
  // FP-CLIENT-SUBMIT-GUARD-01: single-fire the "Tell Story" confirm. The ref is
  // the synchronous guard (state updates a tick late and won't stop a same-frame
  // double-tap); the state drives the pending affordance. This page unmounts on
  // navigate, so the latch resets naturally for a back-then-retry.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const trackRef = useRef(null);
  const dragRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, hasDragged: false });
  const mainRef = useFocusOnMount();

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // ─── Scale constants ───────────────────────────────────────────
    // Inactive scale per breakpoint — detected from card DOM width
    function getMinScale() {
      const cardWidth = track.children[0]?.offsetWidth ?? 344;
      return cardWidth > 500 ? 0.902 : 0.86;
    }

    // ─── Progressive scale on scroll ──────────────────────────────
    // Runs on every scroll frame. Sets transform directly on each card
    // based on its distance from the viewport center. No CSS transition.
    let scalePending = false;

    function updateScale() {
      const minScale = getMinScale();
      const trackCenter = track.scrollLeft + track.clientWidth / 2;

      Array.from(track.children).forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - trackCenter);
        const cardWidth = card.offsetWidth;

        // Normalize distance: 0 = centered, 1 = one card-width away
        const normalized = Math.min(distance / cardWidth, 1);

        // Quadratic ease: card grows quickly as it approaches center
        // normalized=0 → eased=1 (full size), normalized=1 → eased=0 (min size)
        const eased = 1 - normalized * normalized;
        const scale = minScale + (1 - minScale) * eased;

        card.style.transform = `scale(${scale})`;
      });
    }

    function onScroll() {
      if (scalePending) return;
      scalePending = true;
      requestAnimationFrame(() => {
        updateScale();
        scalePending = false;
      });
    }

    track.addEventListener('scroll', onScroll);

    // Set initial scale immediately (first card at scale(1), others smaller)
    updateScale();

    // ─── Snap selection (scrollend) ────────────────────────────────
    // Still needed to update selectedTopic React state for handleConfirm
    function updateSelection() {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let closestIndex = 0;
      let minDist = Infinity;
      Array.from(track.children).forEach((card, i) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const dist = Math.abs(cardCenter - trackCenter);
        if (dist < minDist) {
          minDist = dist;
          closestIndex = i;
        }
      });
      setSelectedTopic(TOPIC_CATEGORIES[closestIndex]);
    }

    track.addEventListener('scrollend', updateSelection);

    // ─── Mouse drag with momentum ──────────────────────────────────
    const drag = dragRef.current;
    // velocity samples: rolling window of { x, t } over last ~5 frames
    drag.samples = [];

    function onMouseDown(e) {
      drag.isDown = true;
      drag.hasDragged = false;
      drag.startX = e.pageX;
      drag.scrollLeft = track.scrollLeft;
      drag.samples = [];
      track.style.cursor = 'grabbing';
      track.style.userSelect = 'none';
    }

    function onMouseMove(e) {
      if (!drag.isDown) return;
      e.preventDefault();
      const dx = drag.startX - e.pageX;
      if (Math.abs(dx) > 4) drag.hasDragged = true;
      track.scrollLeft = drag.scrollLeft + dx;

      // Record velocity sample
      drag.samples.push({ x: e.pageX, t: Date.now() });
      if (drag.samples.length > 5) drag.samples.shift();
    }

    function onMouseUp() {
      if (!drag.isDown) return;
      drag.isDown = false;
      track.style.cursor = '';
      track.style.userSelect = '';

      // Calculate throw velocity from recent samples and fling the carousel.
      // scrollBy({ behavior: 'smooth' }) hands control to the browser:
      // it eases the scroll and CSS scroll-snap handles the final snap point.
      const samples = drag.samples;
      if (samples.length >= 2) {
        const oldest = samples[0];
        const newest = samples[samples.length - 1];
        const dt = newest.t - oldest.t;
        if (dt > 0 && dt < 250) {
          // vx: px/ms. Positive = mouse moved right = scrollLeft should decrease
          const vx = (newest.x - oldest.x) / dt;
          // Project ~350ms of travel. Negate because scroll direction is opposite to drag direction.
          const throwDist = -vx * 350;
          track.scrollBy({ left: throwDist, behavior: 'smooth' });
        }
      }
      drag.samples = [];
    }

    function onMouseLeave() {
      if (!drag.isDown) return;
      // Treat leave same as mouseup — apply throw if there's velocity
      onMouseUp();
    }

    track.addEventListener('mousedown', onMouseDown);
    track.addEventListener('mousemove', onMouseMove);
    track.addEventListener('mouseup', onMouseUp);
    track.addEventListener('mouseleave', onMouseLeave);

    return () => {
      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('scrollend', updateSelection);
      track.removeEventListener('mousedown', onMouseDown);
      track.removeEventListener('mousemove', onMouseMove);
      track.removeEventListener('mouseup', onMouseUp);
      track.removeEventListener('mouseleave', onMouseLeave);
    };
  }, []);

  function handleConfirm() {
    if (submittingRef.current) return;
    if (!selectedTopic) return;
    const learnerId = activeLearner?.id;
    if (!learnerId) return;
    submittingRef.current = true;
    setSubmitting(true);
    navigate('/story/generating', {
      state: { learnerId, topic: selectedTopic.theme },
    });
  }

  return (
    <main className={s.page} ref={mainRef} tabIndex={-1}>

      {/* Theme props — desktop and mobile variants */}
      <div className={s.propsLayer} aria-hidden="true">
        {Object.entries(THEME_PROPS).map(([themeKey, { desktop, mobile }]) => (
          <div
            key={themeKey}
            className={
              selectedTopic?.key === themeKey
                ? `${s.propGroup} ${s.propGroupActive}`
                : s.propGroup
            }
          >
            {desktop.map((prop, i) => (
              <img
                key={`d${i}`}
                src={prop.src}
                alt=""
                className={s.propImgDesktop}
                style={prop.style}
              />
            ))}
            {mobile.map((prop, i) => (
              <img
                key={`m${i}`}
                src={prop.src}
                alt=""
                className={s.propImgMobile}
                style={prop.style}
              />
            ))}
          </div>
        ))}
      </div>

      {/* Cloud band — visible on both mobile and desktop */}
      <CloudBand variant="teal" className={s.cloudBand} />

      {/* Header: Back button + heading */}
      <div className={s.header}>
        <Button
          variant="back"
          onClick={() => navigate('/')}
          className={s.backBtn}
        >
          {t('common.back')}
        </Button>
        <h1 className={s.pageTitle}>
          {t('topics.title')}
        </h1>
      </div>

      {/* Carousel */}
      <div
        className={s.carouselTrack}
        ref={trackRef}
        role="radiogroup"
        aria-label={t('topics.storyThemes')}
      >
        {TOPIC_CATEGORIES.map((topic) => {
          const isActive = selectedTopic?.key === topic.key;
          return (
            <button
              key={topic.key}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => {
                // If this pointer-up ended a drag, let scrollend handle selection.
                if (dragRef.current.hasDragged) return;
                const track = trackRef.current;
                if (!track) return;
                const idx = TOPIC_CATEGORIES.findIndex((cat) => cat.key === topic.key);
                const card = track.children[idx];
                if (!card) return;
                const scrollTarget =
                  card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
                track.scrollTo({ left: scrollTarget, behavior: 'smooth' });
              }}
              className={s.themeCard}
            >
              <div className={s.themeCardBg}>
                <img
                  src={topic.image}
                  alt=""
                  className={s.themeCardBgImg}
                />
                <div className={s.themeCardOverlay} />
              </div>
              <p className={s.themeCardTitle}>{t(topic.titleKey)}</p>
            </button>
          );
        })}
      </div>

      {/* CTA */}
      <div className={s.confirmArea}>
        <button
          type="button"
          onClick={handleConfirm}
          className={`${s.confirmBtn}${submitting ? ` ${s.confirmBtnPending}` : ''}`}
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting && <span className={s.btnSpinner} aria-hidden="true" />}
          {t('topics.tellStory')}
        </button>
      </div>

    </main>
  );
}
